import React, { useEffect, useMemo, useRef, useState } from "react";

// AbsorptionDatePicker — the check-in date selector for the absorption-trends
// panels. `dates` is the rows from ci_get_absorption_dates, each carrying
// `check_in` (the date string key), `date_window_name`, `is_past`, and
// `syncs_tracked`. With 156 pickable windows a flat SelectMenu list was
// unusable, so the selector is now a dropdown date picker: a trigger button
// showing the selected window's `date_window_name` that opens a month-grid
// calendar below it. Dates that have data are clickable, dates without data
// are disabled/muted, past dates are muted-but-clickable, named holidays get
// a colored dot, and the hovered cell shows `date_window_name` as a tooltip.
// The calendar closes when a date is picked or when the user clicks outside.
//
// Props contract is unchanged: `(dates, selectedDate, onChange)`. Both
// AbsorptionHistoryPanel and MarketAbsorptionPanel mount this with
// `selectedDate` starting null and let it self-initialize: the future date
// (`is_past === false`) with the highest `syncs_tracked`, earliest `check_in`
// on tie, is pushed up via onChange once the dates resolve. If there are no
// future windows it falls back to the most recent past date. Month navigation
// opens on the default date's month (not today) so the user lands on the
// window that will actually be selected.

const MONTH_ABBRS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n) => String(n).padStart(2, "0");

// Build a "yyyy-mm-dd" key from local Y/M/D so it matches the `check_in` shape
// the RPC returns — never use UTC here or the day can shift by one.
const toDateKey = (year, monthIndex, day) =>
  `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;

// Named holiday = date_window_name that isn't a generic "Weekend ..."/"Weekday ..."
// label and doesn't begin with a month abbreviation ("Jan", "Feb", ...). Those
// prefixes cover the routine windows; anything else (Christmas, Thanksgiving,
// Labor Day, ...) is a named holiday.
const isNamedHoliday = (name) => {
  if (!name) return false;
  if (name.startsWith("Weekend") || name.startsWith("Weekday")) return false;
  for (const m of MONTH_ABBRS) {
    if (name.startsWith(m)) return false;
  }
  return true;
};

const AbsorptionDatePicker = ({ dates, selectedDate, onChange }) => {
  const rows = Array.isArray(dates) ? dates : [];

  // Lookup map keyed by `check_in` (the date string) → full row object. A Map
  // gives O(1) "does this day have data?" checks while rendering the grid.
  const lookup = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      const key = row?.check_in;
      if (key != null) map.set(String(key), row);
    }
    return map;
  }, [rows]);

  // Default: future date with the most syncs tracked; earliest check_in on
  // tie. Falls back to the most recent past date when no future windows exist.
  const defaultDate = useMemo(() => {
    const future = rows.filter((r) => r?.is_past === false);
    if (future.length > 0) {
      const sorted = [...future].sort((a, b) => {
        const bySyncs = Number(b?.syncs_tracked ?? 0) - Number(a?.syncs_tracked ?? 0);
        if (bySyncs !== 0) return bySyncs;
        return String(a?.check_in).localeCompare(String(b?.check_in));
      });
      return sorted[0]?.check_in ?? null;
    }
    const past = rows.filter((r) => r?.is_past === true);
    if (past.length === 0) return null;
    const pastSorted = [...past].sort((a, b) =>
      String(b?.check_in).localeCompare(String(a?.check_in)),
    );
    return pastSorted[0]?.check_in ?? null;
  }, [rows]);

  // Self-initialize: when the parent left selectedDate unset, push the default
  // up once so the first render already shows the right window. `defaultDate`
  // is memoized on `rows`, so this only fires when dates actually arrive.
  useEffect(() => {
    if (selectedDate == null && defaultDate != null) {
      onChange?.(defaultDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDate]);

  // Displayed month. Start null and snap to the default date's month (not
  // today) once it resolves; the user can then navigate prev/next freely.
  const [viewMonth, setViewMonth] = useState(null);
  useEffect(() => {
    if (viewMonth == null && defaultDate != null) {
      const [y, m] = String(defaultDate).split("-").map(Number);
      if (Number.isFinite(y) && Number.isFinite(m)) {
        setViewMonth(new Date(y, m - 1, 1));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDate]);

  const goPrevMonth = () => {
    if (!viewMonth) return;
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };
  const goNextMonth = () => {
    if (!viewMonth) return;
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };

  // Dropdown open/close state. The calendar grid is hidden by default and
  // revealed by clicking the trigger; it closes again on date pick or a click
  // anywhere outside the picker.
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (rows.length === 0 || viewMonth == null) return null;

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const startDayOfWeek = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build the cell list: `null` entries pad the grid before the 1st so the
  // first real day lands under its weekday header.
  const cells = [];
  for (let i = 0; i < startDayOfWeek; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const selected = selectedDate ?? defaultDate;

  const handleDayClick = (dayKey) => {
    if (!lookup.has(dayKey)) return;
    onChange?.(dayKey);
    setOpen(false);
  };

  const triggerLabel = lookup.get(selected)?.date_window_name ?? "Select a date";

  return (
    <div ref={containerRef} className="flex items-center gap-2 select-none flex-wrap">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Date:</span>
      {/* Trigger + dropdown share this relative wrapper so the calendar opens
      anchored under the trigger button, not under the "Date:" label. */}
      <div className="relative inline-block">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand"
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className={lookup.has(selected) ? "" : "text-gray-400 dark:text-gray-500"}>
            {triggerLabel}
          </span>
          <svg
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Calendar dropdown — absolutely positioned below the trigger so it
        overlays content instead of pushing it down. Hidden unless `open`. */}
        {open && (
          <div className="absolute z-20 left-0 mt-2 p-3 w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {/* Month header + navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={goPrevMonth}
              className="px-2 py-0.5 text-sm text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {MONTH_NAMES[month]} {year}
            </span>
            <button
              type="button"
              onClick={goNextMonth}
              className="px-2 py-0.5 text-sm text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          {/* Weekday header row */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAY_LABELS.map((wd) => (
              <div
                key={wd}
                className="text-center text-[10px] font-medium uppercase text-gray-400 dark:text-gray-500"
              >
                {wd}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (day == null) {
                return <div key={`pad-${idx}`} />;
              }
              const dayKey = toDateKey(year, month, day);
              const row = lookup.get(dayKey);
              const hasData = Boolean(row);
              const past = hasData && row?.is_past === true;
              const isSelected = selected === dayKey;
              const holiday = hasData && isNamedHoliday(row?.date_window_name);
              const tooltip = hasData ? row?.date_window_name ?? dayKey : dayKey;

              const base =
                "relative w-full h-10 flex items-center justify-center rounded text-sm border transition-colors";
              let cls;
              if (!hasData) {
                cls = `${base} text-gray-300 dark:text-gray-600 border-transparent cursor-default`;
              } else if (isSelected) {
                cls = `${base} text-white bg-brand border-brand font-semibold cursor-pointer`;
              } else if (past) {
                cls = `${base} text-gray-400 dark:text-gray-500 border-transparent hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer`;
              } else {
                cls = `${base} text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer`;
              }

              return (
                <button
                  key={dayKey}
                  type="button"
                  title={tooltip}
                  disabled={!hasData}
                  onClick={() => handleDayClick(dayKey)}
                  className={cls}
                >
                  {day}
                  {holiday && (
                    <span
                      className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${
                        isSelected ? "bg-white" : "bg-amber-500"
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </div>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Pick a check-in date to see how its inventory has changed across sync cycles.
      </p>
    </div>
  );
};

export default AbsorptionDatePicker;